import { toCalendarDate } from './isoDate';

const hasOwn = (value, field) =>
  Object.prototype.hasOwnProperty.call(value, field);

const unsupportedValue = () => ({ supported: false, value: '' });

export const isObjectTextSentinel = (value) =>
  typeof value === 'string' &&
  value.trim().toLowerCase() === '[object object]';

const supportedValue = (value) =>
  isObjectTextSentinel(value)
    ? unsupportedValue()
    : { supported: true, value };

const normalizeFormulaResult = (result) => {
  if (
    typeof result === 'string' ||
    (typeof result === 'number' && Number.isFinite(result)) ||
    (result instanceof Date && !Number.isNaN(result.getTime()))
  ) {
    return supportedValue(result);
  }
  return unsupportedValue();
};

// Recognize only object shapes whose displayed text is unambiguous. This is
// deliberately narrower than String(value): arbitrary objects must never turn
// into the corrupt literal "[object Object]".
export const normalizeTextFieldValue = (value) => {
  if (value === undefined || value === null || typeof value === 'string') {
    return supportedValue(value);
  }
  if (typeof value === 'number') {
    return Number.isFinite(value)
      ? supportedValue(value)
      : unsupportedValue();
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime())
      ? unsupportedValue()
      : supportedValue(value);
  }
  if (typeof value !== 'object' || Array.isArray(value)) {
    return unsupportedValue();
  }

  // Excel error values always fail closed, even if a malformed object also
  // contains properties resembling one of the supported shapes.
  if (hasOwn(value, 'error')) {
    return unsupportedValue();
  }

  const isRichText = hasOwn(value, 'richText');
  const isFormula = hasOwn(value, 'formula');
  const isSharedFormula = hasOwn(value, 'sharedFormula');
  const isHyperlink = hasOwn(value, 'text') || hasOwn(value, 'hyperlink');
  const shapeCount =
    Number(isRichText) +
    Number(isFormula || isSharedFormula) +
    Number(isHyperlink);

  // Reject unknown and ambiguous mixed shapes rather than guessing which
  // displayed value the user intended.
  if (shapeCount !== 1 || (isFormula && isSharedFormula)) {
    return unsupportedValue();
  }

  if (isRichText) {
    if (
      Array.isArray(value.richText) &&
      value.richText.every(
        (segment) => segment && typeof segment.text === 'string',
      )
    ) {
      return supportedValue(
        value.richText.map((segment) => segment.text).join(''),
      );
    }
    return unsupportedValue();
  }

  if (isFormula || isSharedFormula) {
    return hasOwn(value, 'result')
      ? normalizeFormulaResult(value.result)
      : unsupportedValue();
  }

  if (
    typeof value.text === 'string' &&
    typeof value.hyperlink === 'string'
  ) {
    return supportedValue(value.text);
  }

  return unsupportedValue();
};

// ExcelJS can omit falsy cached formula results from cell.value while keeping
// them on cell.result. Reading the Cell preserves a legitimate numeric 0.
export const normalizeExcelTextCell = (cell) => {
  const value = cell ? cell.value : undefined;
  if (
    value &&
    typeof value === 'object' &&
    (hasOwn(value, 'formula') || hasOwn(value, 'sharedFormula'))
  ) {
    let result;
    if (cell && 'result' in cell) {
      result = cell.result;
    } else if (hasOwn(value, 'result')) {
      result = value.result;
    }
    // Re-run the complete object-shape checks after recovering the cell-level
    // result. This preserves 0 without letting malformed formula+error,
    // formula+rich-text, or formula+shared-formula objects bypass fail-closed
    // validation.
    return normalizeTextFieldValue({ ...value, result });
  }
  return normalizeTextFieldValue(value);
};

// Text controls and GraphQL String fields need a string, while workbook row
// validation sometimes needs the scalar type above (number/Date). The exact
// sentinel is rejected here whether it was typed or derived from a formula,
// rich-text run, or hyperlink label.
export const formatTextFieldValue = (value) => {
  const normalized = normalizeTextFieldValue(value);
  if (!normalized.supported || isObjectTextSentinel(normalized.value)) {
    return unsupportedValue();
  }
  return {
    supported: true,
    value:
      normalized.value === undefined || normalized.value === null
        ? ''
        : normalized.value instanceof Date
          ? toCalendarDate(normalized.value)
          : String(normalized.value),
  };
};

// The common input callback invokes state updates only after formatting. An
// unsupported value is ignored, preserving the last safe value so InputList's
// render-time validator can never stringify the object first.
export const makeFormattedTextChange = (onChange) => (field, value) => {
  const formatted = formatTextFieldValue(value);
  if (!formatted.supported || typeof onChange !== 'function') {
    return false;
  }
  onChange(field, formatted.value);
  return true;
};

export const requireFormattedTextFieldValue = (value) => {
  const formatted = formatTextFieldValue(value);
  if (!formatted.supported) {
    throw new TypeError(
      'Text fields require plain text or a recognized displayed cell value.',
    );
  }
  return formatted.value;
};
